// Merged date/time helper module derived from src/shared/utils/utilities.jsx and
// src/alive-academics/utils/utilities.js.
import dayjs from 'dayjs'

export const leadingZeroFormatter = new Intl.NumberFormat( undefined, {
    minimumIntegerDigits: 2,
} )

export const getTimePassedFrom = ( time ) => {
    const now = Date.now()
    const createdTime = new Date( time ).getTime()
    const diff = ( now - createdTime ) / 1000
    if ( diff < 60 ) return 'Few seconds ago'
    else if ( diff < 60 * 60 ) {
        const count = Math.floor( diff / 60 )
        return `${count} minute${count > 1 ? 's' : ''} ago`
    } else if ( diff < 60 * 60 * 24 ) {
        const count = Math.floor( diff / 3600 )
        return `${count} hour${count > 1 ? 's' : ''} ago`
    } else if ( diff < 31536000 ) {
        const count = Math.floor( diff / ( 3600 * 24 ) )
        return `${count} day${count > 1 ? 's' : ''} ago`
    } else {
        const count = Math.floor( diff / 31536000 )
        return `${count} Year${count > 1 ? 's' : ''} ago`
    }
}

// getExtraSIfPlural only existed in shared/utils/utilities.jsx (not in alive-academics/utilities.js).
export const getExtraSIfPlural = count => {
    return !( !count || count <= 1 ) ? "s" : ""
}

export const getTimePassedFromDetails = ( time ) => {
    const now = Date.now()
    const createdTime = new Date( time ).getTime()
    const diff = ( now - createdTime ) / 1000

    let displayString = ""

    if ( diff < 60 ) displayString = 'Few seconds ago'
    else if ( diff < 60 * 60 ) {
        const count = Math.floor( diff / 60 )
        displayString = `${count} minute${count > 1 ? 's' : ''} ago`
    } else if ( diff < 60 * 60 * 24 ) {
        const count = Math.floor( diff / 3600 )
        displayString = `${count} hour${count > 1 ? 's' : ''} ago`
    } else if ( diff < 31536000 ) {
        const count = Math.floor( diff / ( 3600 * 24 ) )
        displayString = `${count} day${count > 1 ? 's' : ''} ago`
    } else {
        const count = Math.floor( diff / 31536000 )
        displayString = `${count} Year${count > 1 ? 's' : ''} ago`
    }

    return {
        displayString,
        diff,
        h: Math.floor( diff / 3600 ),
        m: Math.floor( ( diff % 3600 ) / 60 ),
        s: Math.floor( ( diff % 3600 ) % 60 ),
    }
}

// CONFLICT: shared/utils/utilities.jsx did not handle negative `time`. alive-academics/utilities.js
// added Math.abs(time) handling plus a leading '-' sign for negative durations. Kept the
// alive-academics version per the merge preference rule (it is a strict behavioral superset).
export const formatVideoDuration = ( time ) => {
    let t = Math.abs( time )
    const seconds = Math.floor( t % 60 )
    const minutes = Math.floor( t / 60 ) % 60
    const hours = Math.floor( t / 3600 )
    if ( hours === 0 ) {
        return `${time < 0 ? "-" : ""}${minutes}:${leadingZeroFormatter.format( seconds )}`
    } else {
        return `${time < 0 ? "-" : ""}${hours}:${leadingZeroFormatter.format( minutes )}:${leadingZeroFormatter.format( seconds )}`
    }
}

export const secondsToHMS = ( time, showZeroValues = true ) => {
    time = Number( time )
    var h = Math.floor( time / 3600 )
    var m = Math.floor( ( time % 3600 ) / 60 )
    var s = Math.floor( ( time % 3600 ) % 60 )

    if ( showZeroValues )
        return `${leadingZeroFormatter.format( h )}:${leadingZeroFormatter.format( m )}:${leadingZeroFormatter.format( s )}`
    return `${h > 0 ? `${leadingZeroFormatter.format( h )}:` : ""}${leadingZeroFormatter.format( m )}:${leadingZeroFormatter.format( s )}`
}

// minutesToString only existed in shared/utils/utilities.jsx (not in alive-academics/utilities.js).
export function minutesToString ( totalMinutes, options = {} ) {
    if ( typeof totalMinutes !== 'number' || totalMinutes < 0 ) return ''

    const hours = Math.floor( totalMinutes / 60 )
    const minutes = totalMinutes % 60

    const { short = false, tiny = false } = options

    // Define label maps based on flags
    const getLabels = ( value, unit ) => {
        if ( tiny ) {
            return unit === 'hour' ? 'H' : 'M'
        }
        if ( short ) {
            return unit === 'hour' ? 'hr' : 'min'
        }
        // Full labels with pluralization
        const isPlural = value !== 1
        return unit === 'hour'
            ? ( isPlural ? 'Hours' : 'Hour' )
            : ( isPlural ? 'Minutes' : 'Minute' )
    }

    const parts = []

    if ( hours > 0 ) {
        parts.push( `${hours}${tiny ? '' : ' '}${getLabels( hours, 'hour' )}` )
    }

    // Show minutes if present or if total duration is 0
    if ( minutes > 0 || hours === 0 ) {
        parts.push( `${minutes}${tiny ? '' : ' '}${getLabels( minutes, 'minute' )}` )
    }

    return parts.join( ' ' )
}

// getCurrentWeek only existed in alive-academics/utils/utilities.js
export const getCurrentWeek = ( date = new Date() ) => {
    const curr = new Date( date )
    const dday = curr.getDay()
    let week = []
    if ( dday === 0 ) {
        for ( let i = 5; i >= -1; i-- ) {
            let tempCurr = new Date( date )
            let first = tempCurr.getDate() - i
            let day = new Date( tempCurr.setDate( first ) ).toISOString().slice( 0, 10 )
            week.push( day )
        }
    } else if ( dday !== 0 ) {
        for ( let i = 1; i <= 6; i++ ) {
            let first = curr.getDate() - curr.getDay() + i + 1
            let day = new Date( curr.setDate( first ) ).toISOString().slice( 0, 10 )
            week.push( day )
        }
        week.push( new Date( curr.setDate( curr.getDate() + 1 ) ).toISOString().slice( 0, 10 ) )
    }

    return week
}

/**
 * Returns an array of evenly spaced time values across the video duration.
 * Includes the first frame (0) and the last frame (duration) when n > 1.
 * (getTimelineIntervals only existed in alive-academics/utils/utilities.js)
 *
 * @param {number} duration - Video duration in seconds.
 * @param {number} count - Number of timeline points required.
 * @returns {number[]} Sorted time values from lowest to highest.
 */
export const getTimelineIntervals = ( duration, count, options = {} ) => {

    const { includeExtreams = true } = options

    const totalPoints = Math.max( 0, Math.floor( count + ( !includeExtreams ? 2 : 0 ) ) )
    if ( !Number.isFinite( duration ) || duration <= 0 || totalPoints <= 0 ) return []
    if ( totalPoints === 1 ) return [0]

    const step = duration / ( totalPoints - 1 )
    const intervals = Array.from( { length: totalPoints }, ( _, index ) => {
        const time = index * step
        return Number( time.toFixed( 3 ) )
    } )

    if ( includeExtreams )
        return intervals
    else
        return intervals.slice( 1, -1 )
}

export const time24to12 = ( time ) => {
    try {
        if ( time ) {
            const formatedTime = new Date( `2024-01-01 ${time}` ).toLocaleTimeString( 'en-IN', { hour: '2-digit', minute: '2-digit' } )
            if ( formatedTime !== 'Invalid Date' ) return formatedTime.toUpperCase()
            else return ''
        } else return ''
    } catch ( err ) {
        console.log( 'ERROR', err )
        return time
    }
}

// formatDate and adjustDate only existed in alive-academics/utils/utilities.js
export const formatDate = ( datetime, formatOptions = {}, adjustValue = { minutes: 0, hours: 0, seconds: 0 } ) => {
    if ( datetime ) {
        datetime = dayjs( datetime )
        if ( typeof adjustValue !== 'object' ) datetime = datetime.add( +( adjustValue || 0 ), 'minutes' )
        else
            datetime = datetime
                .add( adjustValue.hours || 0, 'hours' )
                .add( adjustValue.minutes || 0, 'minutes' )
                .add( adjustValue.seconds || 0, 'seconds' )
        if ( formatOptions.showTime ) {
            let timeString = datetime.format( 'h:mm a' )
            datetime = datetime.toString()
            return new Date( datetime ).toLocaleDateString( formatOptions.regionCode || 'en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', ...formatOptions } ).replace( /\//g, formatOptions.separator || '-' ) + ` ${timeString}`;
        } else if ( formatOptions.format ) {
            return datetime.format( formatOptions.format )
        } else {
            datetime = datetime.toString()
            return new Date( datetime ).toLocaleDateString( formatOptions.regionCode || 'en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', ...formatOptions } ).replace( /\//g, formatOptions.separator || '-' );
        }
    }
    return ''
}

export const adjustDate = ( datetime, adjustValue = { minutes: 0, hours: 0, seconds: 0 } ) => {
    if ( datetime ) {
        datetime = dayjs( datetime )
        if ( typeof adjustValue !== 'object' ) datetime = datetime.add( +( adjustValue || 0 ), 'minutes' )
        else
            datetime = datetime
                .add( adjustValue.hours || 0, 'hours' )
                .add( adjustValue.minutes || 0, 'minutes' )
                .add( adjustValue.seconds || 0, 'seconds' )
        datetime = datetime.toString()
        return new Date( datetime )
    }
    return ''
}
